import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useUser from "../../hooks/useUser";
import { useListings } from "../../hooks/useListings";
import { useCategories } from "../../hooks/useCategories";
import { usePayment } from "../../hooks/usePayment";

const MyPostsPage = () => {
  const [posts, setPosts] = useState([]);
  const { getMyPosts, loading } = useUser();
  const { removeListing } = useListings();
  const { categories } = useCategories();
  const { getPaymentIntent } = usePayment();
  const navigate = useNavigate();

  // Modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [editForm, setEditForm] = useState({ post_title: "", description: "" });
  const [editFormFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    getMyPosts(token)
      .then((data) => {
        setPosts(data);
        console.log("My posts fetched successfully:", data);
      })
      .catch(() => setPosts([]));
  }, [navigate]);

  const handleExtendTime = (postId) => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
    }
    alert(`Extend post time feature coming soon for post ${postId}!`);
  };
  const handleCompletePayment = async (postId) => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    navigate(`/payment/${postId}`);
  };
  // Save edit
  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!selectedPost) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }
      // Prepare images as array
      const formToSend = { ...editForm };
      formToSend.images = editForm.images
        ? editForm.images
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [];
      // Prepare attributes (exclude known fields)
      const knownFields = [
        "post_title",
        "title",
        "description",
        "category_id",
        "images",
        "contact_email",
        "relay_email",
        "contact_name",
        "phone_number",
        "phone_extension",
        "show_phone_number",
        "phone_calls_ok",
        "text_sms_ok",
        "show_post_to_disabled_people",
        "country",
        "city",
        "region",
      ];
      formToSend.attributes = {};
      Object.keys(editForm).forEach((key) => {
        if (!knownFields.includes(key)) {
          formToSend.attributes[key] = editForm[key];
        }
      });
      setPosts((prev) =>
        prev.map((p) =>
          p.id === selectedPost.id ? { ...p, ...formToSend } : p
        )
      );
      setEditModalOpen(false);
    } catch (err) {
      alert("Failed to update post.");
    } finally {
      setSaving(false);
    }
  };

  // Open delete modal
  const handleDelete = (post) => {
    setSelectedPost(post);
    setDeleteModalOpen(true);
  };

  // Confirm delete
  const handleDeleteConfirm = async () => {
    if (!selectedPost) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }
      await removeListing(selectedPost.id, token);
      setPosts((prev) => prev.filter((p) => p.id !== selectedPost.id));
      setDeleteModalOpen(false);
    } catch (err) {
      alert("Failed to delete post.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto mt-8 px-4 text-[15px] text-gray-800 font-sans">
      <h1 className="text-xl font-bold mb-4">my posts</h1>
      {loading ? (
        <div className="text-gray-600">Loading...</div>
      ) : posts.length === 0 ? (
        <div className="text-gray-600">You have no posts.</div>
      ) : (
        <ul className="space-y-4">
          {posts
            .filter((post) => !post.is_deleted)
            .map((post) => {
              const isExpired =
                post.expires_at && new Date(post.expires_at) < new Date();
              return (
                <li key={post.id} className="border-b border-gray-200 pb-2">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div className="w-full">
                      <a
                        href={`/listings/${post.id}`}
                        className="text-blue-700 underline hover:text-blue-900 font-medium"
                      >
                        {post.post_title || post.title || "(no title)"}
                      </a>
                      <span className="ml-2 text-xs text-gray-500">
                        {post.created_at
                          ? new Date(post.created_at).toLocaleDateString()
                          : ""}
                      </span>
                      {/* More Info */}
                      <div className="mt-1 text-sm text-gray-700">
                        {post.description && (
                          <div>
                            <span className="font-semibold">Description:</span>{" "}
                            {post.description}
                          </div>
                        )}
                        {post.category && (
                          <div>
                            <span className="font-semibold">Category:</span>{" "}
                            {post.category}
                          </div>
                        )}
                        {post.status && (
                          <div>
                            <span className="font-semibold">Status:</span>{" "}
                            {post.status}
                          </div>
                        )}
                        {Array.isArray(post.images) && (
                          <div>
                            <span className="font-semibold">Images:</span>{" "}
                            {post.images.length}
                          </div>
                        )}
                      </div>
                      {/* Placeholders for future features */}
                      <div className="mt-2 flex flex-wrap gap-4 text-xs text-gray-500">
                        <div className="bg-gray-200 rounded px-2 py-1">
                          Views: —
                        </div>
                        <div className="bg-gray-200 rounded px-2 py-1">
                          Impressions: —
                        </div>
                        <div className="bg-gray-200 rounded px-2 py-1">
                          Insights: <span className="italic">Coming soon</span>
                        </div>
                      </div>
                      {/* Custom Buttons */}
                      <div className="mt-2 flex gap-2">
                        {isExpired && (
                          <button
                            className="px-2 py-1 rounded bg-yellow-500 text-white hover:bg-yellow-600 text-xs"
                            onClick={() => handleExtendTime(post.id)}
                          >
                            Extend Post Time
                          </button>
                        )}
                        {!post.is_paid && (
                          <button
                            className="px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 text-xs"
                            onClick={() => handleCompletePayment(post.id)}
                          >
                            Complete Payment
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 sm:mt-0 flex gap-4 text-sm">
                      <button
                        className="text-blue-700 underline hover:text-blue-900"
                        onClick={() => navigate(`/edit-post/${post.id}`)}
                      >
                        edit
                      </button>
                      <button
                        className="text-blue-700 underline hover:text-blue-900"
                        onClick={() => handleDelete(post)}
                      >
                        delete
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
        </ul>
      )}

      {/* Edit Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md overflow-y-auto max-h-[90vh]">
            <h2 className="text-lg font-bold mb-4">Edit Post</h2>
            <form onSubmit={handleEditSave} className="space-y-4">
              {/* Static fields */}
              <div>
                <label className="block text-sm font-medium mb-1">Title</label>
                <input
                  type="text"
                  className="border border-gray-300 px-2 py-1 w-full rounded"
                  value={editForm.post_title}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, post_title: e.target.value }))
                  }
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Description
                </label>
                <textarea
                  className="border border-gray-300 px-2 py-1 w-full rounded"
                  value={editForm.description}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, description: e.target.value }))
                  }
                  rows={3}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Category
                </label>
                <select
                  className="border border-gray-300 px-2 py-1 w-full rounded"
                  value={editForm.category_id}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, category_id: e.target.value }))
                  }
                  required
                >
                  <option value="">Select category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Images (comma-separated URLs)
                </label>
                <input
                  type="text"
                  className="border border-gray-300 px-2 py-1 w-full rounded"
                  value={editForm.images}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, images: e.target.value }))
                  }
                />
                {editForm.images && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {editForm.images.split(",").map((url, i) => (
                      <img
                        key={i}
                        src={url.trim()}
                        alt="img"
                        className="h-12 w-12 object-cover border rounded"
                      />
                    ))}
                  </div>
                )}
              </div>
              {/* Template (attribute) fields */}
              {editFormFields.length > 0 && (
                <div>
                  <div className="font-semibold text-xs mb-2 text-gray-700 uppercase tracking-wider">
                    Additional Information
                  </div>
                  {editFormFields.map((field, idx) => {
                    const key = `attr_${field.label}`;
                    if (field.type === "text" || field.type === "number") {
                      return (
                        <div key={key} className="mb-2">
                          <label className="block text-xs font-medium mb-1">
                            {field.label}
                          </label>
                          <input
                            type={field.type}
                            className="border border-gray-300 px-2 py-1 w-full rounded"
                            value={editForm[key] || ""}
                            onChange={(e) =>
                              setEditForm((f) => ({
                                ...f,
                                [key]: e.target.value,
                              }))
                            }
                            required={field.required}
                          />
                        </div>
                      );
                    }
                    if (field.type === "dropdown") {
                      return (
                        <div key={key} className="mb-2">
                          <label className="block text-xs font-medium mb-1">
                            {field.label}
                          </label>
                          <select
                            className="border border-gray-300 px-2 py-1 w-full rounded"
                            value={editForm[key] || ""}
                            onChange={(e) =>
                              setEditForm((f) => ({
                                ...f,
                                [key]: e.target.value,
                              }))
                            }
                            required={field.required}
                          >
                            <option value="">select...</option>
                            {field.options?.map((opt, i) => (
                              <option key={i} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      );
                    }
                    if (field.type === "checkbox") {
                      return (
                        <div key={key} className="flex items-center gap-2 mb-2">
                          <input
                            type="checkbox"
                            checked={!!editForm[key]}
                            onChange={(e) =>
                              setEditForm((f) => ({
                                ...f,
                                [key]: e.target.checked,
                              }))
                            }
                          />
                          <label className="text-xs">{field.label}</label>
                        </div>
                      );
                    }
                    // Add more types as needed
                    return null;
                  })}
                </div>
              )}
              {/* Contact and other static fields ... (keep as before) */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Contact Email
                </label>
                <input
                  type="email"
                  className="border border-gray-300 px-2 py-1 w-full rounded"
                  value={editForm.contact_email}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      contact_email: e.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Relay Email
                </label>
                <input
                  type="checkbox"
                  checked={!!editForm.relay_email}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      relay_email: e.target.checked,
                    }))
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Contact Name
                </label>
                <input
                  type="text"
                  className="border border-gray-300 px-2 py-1 w-full rounded"
                  value={editForm.contact_name}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, contact_name: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Phone Number
                </label>
                <input
                  type="text"
                  className="border border-gray-300 px-2 py-1 w-full rounded"
                  value={editForm.phone_number}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, phone_number: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Phone Extension
                </label>
                <input
                  type="text"
                  className="border border-gray-300 px-2 py-1 w-full rounded"
                  value={editForm.phone_extension}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      phone_extension: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!editForm.show_phone_number}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      show_phone_number: e.target.checked,
                    }))
                  }
                />
                <label className="text-sm">Show Phone Number</label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!editForm.phone_calls_ok}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      phone_calls_ok: e.target.checked,
                    }))
                  }
                />
                <label className="text-sm">Phone Calls OK</label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!editForm.text_sms_ok}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      text_sms_ok: e.target.checked,
                    }))
                  }
                />
                <label className="text-sm">Text/SMS OK</label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!editForm.show_post_to_disabled_people}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      show_post_to_disabled_people: e.target.checked,
                    }))
                  }
                />
                <label className="text-sm">Show Post to Disabled People</label>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Country
                </label>
                <input
                  type="text"
                  className="border border-gray-300 px-2 py-1 w-full rounded"
                  value={editForm.country}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, country: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">City</label>
                <input
                  type="text"
                  className="border border-gray-300 px-2 py-1 w-full rounded"
                  value={editForm.city}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, city: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Region</label>
                <input
                  type="text"
                  className="border border-gray-300 px-2 py-1 w-full rounded"
                  value={editForm.region}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, region: e.target.value }))
                  }
                />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  className="px-4 py-2 rounded bg-gray-200 text-gray-700"
                  onClick={() => setEditModalOpen(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm">
            <h2 className="text-lg font-bold mb-4 text-red-600">Delete Post</h2>
            <p className="mb-4">
              Are you sure you want to delete the post{" "}
              <span className="font-semibold">
                {selectedPost?.post_title || selectedPost?.title}
              </span>
              ? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 rounded bg-gray-200 text-gray-700"
                onClick={() => setDeleteModalOpen(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
                onClick={handleDeleteConfirm}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyPostsPage;
